import { createFileRoute, redirect, useNavigate } from '@tanstack/react-router'
/**
 * Checkout Page
 *
 * This page handles the payment flow for upgrading to a paid plan.
 * It uses Stripe Elements to securely collect payment information.
 */

import {
  Badge,
  Button,
  Card,
  Content as CardContent,
  Text as CardDescription,
  Header as CardHeader,
  Heading as CardTitle,
  InlineAlert,
} from '@react-spectrum/s2'
import { PaymentElement, useElements, useStripe } from '@stripe/react-stripe-js'
import { CheckCircle2, Loader2, Lock, ShieldCheck } from 'lucide-react'
import { useRef, useState } from 'react'
import { APP_ROUTES } from '@shared/app-routes'
import {
  getAnalyticsRequestContext,
  trackAnalyticsEvent,
} from '@client/analytics'
import { getPublicPlanCard } from '@rpc/plans.rpc'
import { createPaymentIntent, getStripePublishableKey } from '@rpc/billing.rpc'
import type { FormEvent } from 'react'
import { StripeProvider } from '@/components/StripeProvider'
import Header from '@/components/Header'
import Footer from '@/components/Footer'
import { fullWidthButtonStyles, fullWidthStyles } from '@/styles/s2-macros'

// Define the route with proper types
const checkoutRoute = createFileRoute('/checkout/$planId')({
  loader: async ({ params }) => {
    const { planId } = params

    // Validate planId
    if (!['premiumUser', 'proUser'].includes(planId)) {
      throw redirect({ to: APP_ROUTES.dashboard.home })
    }
    const validPlanId = planId as 'premiumUser' | 'proUser'

    return {
      planId: validPlanId,
      plan: await getPublicPlanCard({ data: { planId: validPlanId } }),
      stripePublishableKey: (await getStripePublishableKey()).publishableKey,
    }
  },
  component: CheckoutPage,
})

// Inner checkout form component that has access to Stripe context
function CheckoutForm({
  planId,
  amount,
  checkoutId,
  checkoutAttemptId,
  currency,
}: {
  planId: string
  amount: number
  checkoutId: string
  checkoutAttemptId: string
  currency: string
}) {
  const navigate = useNavigate()
  const stripe = useStripe()
  const elements = useElements()

  const [error, setError] = useState<string | null>(null)
  const [processing, setProcessing] = useState(false)
  const [succeeded, setSucceeded] = useState(false)

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault()

    if (!stripe || !elements) {
      return
    }

    setError(null)
    setProcessing(true)
    trackAnalyticsEvent('checkout_payment_submitted', {
      checkout_attempt_id: checkoutAttemptId,
      checkout_id: checkoutId,
      plan_id: planId,
      amount_minor: Math.round(amount * 100),
      currency,
    })

    try {
      const { error: submitError } = await stripe.confirmPayment({
        elements,
        confirmParams: {
          return_url: `${window.location.origin}/checkout/success?planId=${planId}`,
        },
        redirect: 'if_required',
      })

      if (submitError) {
        setError(submitError.message ?? 'Payment failed')
        trackAnalyticsEvent('checkout_payment_failed', {
          checkout_attempt_id: checkoutAttemptId,
          checkout_id: checkoutId,
          plan_id: planId,
          amount_minor: Math.round(amount * 100),
          currency,
          stage: 'confirm_payment',
        })
      } else {
        setSucceeded(true)
        trackAnalyticsEvent('checkout_payment_confirmation_received', {
          checkout_attempt_id: checkoutAttemptId,
          checkout_id: checkoutId,
          plan_id: planId,
          amount_minor: Math.round(amount * 100),
          currency,
        })
        // Redirect to success page
        setTimeout(() => {
          navigate({
            to: '/checkout/success',
            search: { planId },
          })
        }, 1500)
      }
    } catch (err) {
      setError(
        err instanceof Error ? err.message : 'An unexpected error occurred',
      )
      trackAnalyticsEvent('checkout_payment_failed', {
        checkout_attempt_id: checkoutAttemptId,
        checkout_id: checkoutId,
        plan_id: planId,
        amount_minor: Math.round(amount * 100),
        currency,
        stage: 'confirm_payment',
      })
    } finally {
      setProcessing(false)
    }
  }

  if (succeeded) {
    return (
      <div className="mx-auto w-full max-w-md">
        <Card styles={fullWidthStyles}>
          <CardContent>
            <div className="space-y-4 pt-6 text-center">
              <div className="flex justify-center">
                <CheckCircle2 className="h-16 w-16 text-green-500" />
              </div>
              <h3 className="text-xl font-semibold">Payment successful!</h3>
              <p className="text-sm text-neutral-500">
                Redirecting you to your success page...
              </p>
            </div>
          </CardContent>
        </Card>
      </div>
    )
  }

  return (
    <form onSubmit={handleSubmit} className="space-y-6">
      <Card styles={fullWidthStyles}>
        <CardHeader>
          <CardTitle>Payment details</CardTitle>
          <CardDescription>
            Enter your card information below. Your payment is secure.
          </CardDescription>
        </CardHeader>
        <CardContent>
          <PaymentElement
            id="payment-element"
            options={{
              layout: 'tabs',
            }}
          />
        </CardContent>
      </Card>

      {error && (
        <InlineAlert variant="negative" fillStyle="subtleFill">
          <div className="text-sm [&_p]:leading-relaxed">{error}</div>
        </InlineAlert>
      )}

      <Button
        type="submit"
        styles={fullWidthButtonStyles}
        size="L"
        isDisabled={processing || !stripe || !elements}
      >
        {processing ? (
          <>
            <Loader2 className="mr-2 h-4 w-4 animate-spin" />
            Processing...
          </>
        ) : (
          `Pay £${amount.toFixed(2)}`
        )}
      </Button>
    </form>
  )
}

function CheckoutPage() {
  const { planId, plan, stripePublishableKey } = checkoutRoute.useLoaderData()
  const [clientSecret, setClientSecret] = useState<string | null>(null)
  const [checkoutId, setCheckoutId] = useState<string | null>(null)
  const [amount, setAmount] = useState<number | null>(null)
  const [currency, setCurrency] = useState('GBP')
  const [intentError, setIntentError] = useState<string | null>(null)
  const [isPreparingPayment, setIsPreparingPayment] = useState(false)
  const checkoutAttemptId = useRef(globalThis.crypto.randomUUID()).current

  const handleBeginCheckout = async () => {
    setIntentError(null)
    setIsPreparingPayment(true)
    trackAnalyticsEvent('checkout_started', {
      checkout_attempt_id: checkoutAttemptId,
      plan_id: planId,
      currency: 'GBP',
    })

    try {
      const analytics = await getAnalyticsRequestContext()
      const result = await createPaymentIntent({
        data: {
          planId,
          checkoutAttemptId,
          analytics: {
            visitorId: analytics.visitorId,
            sessionId: analytics.sessionId,
            googleClientId: analytics.googleClientId,
            googleSessionId: analytics.googleSessionId,
            googleEngagementTimeMsec: analytics.googleEngagementTimeMsec,
            amplitudeSessionId: analytics.amplitudeSessionId,
          },
        },
      })
      setClientSecret(result.clientSecret)
      setCheckoutId(result.paymentIntentId)
      setAmount(result.amount)
      setCurrency(result.currency)
      trackAnalyticsEvent('checkout_payment_prepared', {
        checkout_attempt_id: checkoutAttemptId,
        checkout_id: result.paymentIntentId,
        plan_id: planId,
        amount_minor: Math.round(result.amount * 100),
        currency: result.currency,
      })
    } catch (error) {
      setIntentError(
        error instanceof Error
          ? error.message
          : 'Could not prepare checkout. Please try again.',
      )
      trackAnalyticsEvent('checkout_payment_prepare_failed', {
        checkout_attempt_id: checkoutAttemptId,
        plan_id: planId,
        currency: 'GBP',
      })
    } finally {
      setIsPreparingPayment(false)
    }
  }

  const displayPrice =
    amount == null ? plan.price : `£${amount.toFixed(2)}/month`

  return (
    <div className="flex flex-col min-h-screen bg-neutral-50 dark:bg-neutral-900">
      <Header />
      <main id="main-content" className="flex-1 py-12 px-4">
        <div className="max-w-5xl mx-auto space-y-8">
          <section className="rounded-[2rem] border border-border/70 bg-gradient-to-br from-white via-white to-sky-50/70 p-6 shadow-[0_24px_80px_-40px_rgba(15,23,42,0.3)] dark:from-neutral-950 dark:via-neutral-950 dark:to-sky-950/20 md:p-8">
            <div className="flex flex-col gap-6 lg:flex-row lg:items-start lg:justify-between">
              <div className="space-y-3">
                <div className="inline-flex items-center gap-2 rounded-full border border-primary/20 bg-white/80 px-3 py-1 text-xs font-semibold uppercase tracking-[0.22em] text-primary dark:bg-neutral-950/60">
                  <Lock className="h-3.5 w-3.5" />
                  Secure checkout
                </div>
                <div className="space-y-2">
                  <h1 className="text-3xl font-bold tracking-tight text-neutral-900 dark:text-neutral-100">
                    Upgrade to {plan.name}
                  </h1>
                  <p className="max-w-2xl text-sm leading-6 text-neutral-600 dark:text-neutral-300">
                    Complete your subscription to unlock the selected workspace
                    access and usage limits.
                  </p>
                </div>
              </div>
              <Badge variant="accent" fillStyle="subtle">
                {displayPrice}
              </Badge>
            </div>
          </section>

          <div className="grid gap-6 lg:grid-cols-[minmax(0,0.95fr)_minmax(0,1.05fr)]">
            <Card styles={fullWidthStyles}>
              <CardHeader>
                <CardTitle>Order summary</CardTitle>
                <CardDescription>
                  What you’re subscribing to and what’s included.
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  <div className="rounded-[1.6rem] border border-border/80 bg-gradient-to-br from-sky-50 via-white to-cyan-50 p-5 dark:from-sky-950/20 dark:via-neutral-950 dark:to-cyan-950/20">
                    <div className="flex justify-between items-center">
                      <div>
                        <p className="font-semibold">{plan.name} Plan</p>
                        <p className="text-sm text-neutral-500">
                          Monthly subscription
                        </p>
                      </div>
                      <p className="text-lg font-bold">{displayPrice}</p>
                    </div>
                  </div>

                  <div className="rounded-2xl border border-border/80 bg-background px-4 py-4">
                    <div className="flex items-start gap-3">
                      <div className="mt-0.5 rounded-xl bg-primary/10 p-2 text-primary">
                        <ShieldCheck className="h-4 w-4" />
                      </div>
                      <div>
                        <p className="text-sm font-semibold text-foreground">
                          Protected payment flow
                        </p>
                        <p className="mt-1 text-sm leading-6 text-muted-foreground">
                          Card details are collected securely through Stripe
                          Elements.
                        </p>
                      </div>
                    </div>
                  </div>

                  <div className="border-t pt-4">
                    <ul className="space-y-2 text-sm">
                      {plan.features.slice(0, 4).map((feature, i) => (
                        <li key={i} className="flex items-center gap-2">
                          <CheckCircle2 className="h-4 w-4 text-green-500" />
                          <span>{feature}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                </div>
              </CardContent>
            </Card>

            {clientSecret && checkoutId && amount != null ? (
              <StripeProvider
                clientSecret={clientSecret}
                publishableKey={stripePublishableKey}
              >
                <CheckoutForm
                  planId={planId}
                  amount={amount}
                  checkoutId={checkoutId}
                  checkoutAttemptId={checkoutAttemptId}
                  currency={currency}
                />
              </StripeProvider>
            ) : (
              <Card styles={fullWidthStyles}>
                <CardHeader>
                  <CardTitle>Prepare secure payment</CardTitle>
                  <CardDescription>
                    We only create the Stripe payment session once you choose to
                    continue.
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="space-y-4">
                    <p className="text-sm leading-6 text-muted-foreground">
                      Review the order summary, then continue to load the secure
                      card form.
                    </p>
                    {intentError ? (
                      <InlineAlert variant="negative" fillStyle="subtleFill">
                        <div className="text-sm [&_p]:leading-relaxed">
                          {intentError}
                        </div>
                      </InlineAlert>
                    ) : null}
                    <Button
                      styles={fullWidthButtonStyles}
                      size="L"
                      onPress={() => void handleBeginCheckout()}
                      isDisabled={isPreparingPayment}
                    >
                      {isPreparingPayment ? (
                        <>
                          <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                          Preparing payment...
                        </>
                      ) : (
                        'Continue to payment'
                      )}
                    </Button>
                  </div>
                </CardContent>
              </Card>
            )}
          </div>
        </div>
      </main>

      <Footer />
    </div>
  )
}

export { checkoutRoute as Route }
